import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  Textarea,
} from "@heroui/react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { createOrderFromQuotation, type Project } from "@/features/projects/api";
import { errorMessage } from "@/lib/api";
import { previewQuotationOrder } from "./api";

/** 今日（ローカル時刻）を YYYY-MM-DD で返す。 */
function today(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * 見積を受注にするダイアログ。
 *
 * **案件を起票するか、既にある案件に結び付けるか**は、その見積が案件から起こされた
 * ものかどうかで決まる（[[urgent-project-flow]]）。利用者から見て「受注にする」は
 * 1 つの操作なので、押す場所もダイアログも 1 つにしてある。
 *
 * - 通常フロー … 案件を起票する。得意先・営業担当は見積から引き継ぎ、明細は
 *   **図面行だけ**がコピーされて紐付けが張られる（運搬費・値引きの行は製作対象
 *   ではないため）。工番・納期などをここで入れる
 * - 特急フロー（``originProjectNumber`` あり）… **案件は既にある**ので何も入力
 *   させない。図番が一致する行を結び、案件側にしかない図面を見積からも見えるよう
 *   にする
 *
 * どちらも最後に見積の状態が「受注」になり、以後は直せなくなる。
 */
export default function OrderDialog({
  isOpen,
  quotationId,
  quotationNumber,
  itemCount,
  originProjectNumber,
  onClose,
  onCreated,
}: {
  isOpen: boolean;
  quotationId: number;
  quotationNumber: string;
  /** 図面行の数。コピーされる行数の目安として見せる。 */
  itemCount: number;
  /**
   * 起票元の案件の工番（特急フロー）。入っていると**案件を作らず結び付ける**
   * ので、入力欄を出さずに相手だけを見せる。
   */
  originProjectNumber?: string | null;
  onClose: () => void;
  onCreated: (project: Project) => void;
}) {
  /** 特急フロー（案件が先）か。入力させるものが無くなる。 */
  const attaching = originProjectNumber != null;

  // 押すと見積が受注済みになって直せなくなる（[[quotation_lock]]）ので、何が
  // 起きるかを押す前に見せる。実行と同じ計画を通すので結果と食い違わない。
  const preview = useQuery({
    queryKey: ["order-preview", quotationId],
    queryFn: () => previewQuotationOrder(quotationId),
    enabled: isOpen && attaching,
  });
  const matched = preview.data?.matched ?? [];
  const differing = matched.filter((row) => row.quantity_differs);
  const leftOver = [
    ...(preview.data?.unmatched_quotation_items ?? []).map((row) => ({
      ...row,
      side: "見積" as const,
    })),
    ...(preview.data?.unmatched_project_items ?? []).map((row) => ({
      ...row,
      side: "案件" as const,
    })),
  ];
  const [title, setTitle] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [orderDate, setOrderDate] = useState(today());
  const [deadline, setDeadline] = useState("");
  const [shippingPlannedDate, setShippingPlannedDate] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setTitle("");
    setPoNumber("");
    setOrderDate(today());
    setDeadline("");
    setShippingPlannedDate("");
    setNote("");
    setError(null);
    setSaving(false);
  }, [isOpen]);

  const submit = async () => {
    setSaving(true);
    try {
      onCreated(
        await createOrderFromQuotation(
          quotationId,
          // 特急フローは案件が既にあるので、工番も納期も送らない（API も無視する）。
          attaching
            ? {}
            : {
                project_title: title.trim() || null,
                client_po_number: poNumber.trim() || null,
                order_date: orderDate || null,
                deadline: deadline || null,
                shipping_planned_date: shippingPlannedDate || null,
                note: note.trim() || null,
              },
        ),
      );
      onClose();
    } catch (e) {
      setError(errorMessage(e, "受注にできませんでした。"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size="2xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-1">
          <span>この見積を受注にする</span>
          <span className="text-small font-normal text-default-500">
            {attaching ? (
              <>
                {quotationNumber} を案件 {originProjectNumber}{" "}
                に結び付けます。案件は新しく作りません。図番が一致する明細どうしを結び、案件の図面がこの見積からも見えるようになります。見積の状態は「受注」になり、以後は直せなくなります。
              </>
            ) : (
              <>
                {quotationNumber}{" "}
                から案件を起票します。得意先・営業担当は引き継ぎ、図面の明細（
                {itemCount}
                行）をコピーして見積と紐付けます。運搬費・値引きの行はコピーされません。見積の状態は「受注」になります。
              </>
            )}
          </span>
        </ModalHeader>

        <ModalBody>
          {/* 特急フローでは案件が既にあるので、工番も納期もここでは決めない。
              入力欄を出すと、案件の値を上書きするように読める。 */}
          {attaching ? (
            preview.isPending ? (
              <div className="flex justify-center py-6">
                <Spinner label="内訳を確かめています..." />
              </div>
            ) : (
              <div className="flex flex-col gap-3 text-sm">
                <p className="text-default-600">
                  図番が一致する <b>{matched.length} 行</b> を結びます。
                </p>

                {/* 数量の食い違いは止めないが、黙って結ぶと「見積と違う数を
                    作っている」ことに誰も気づけない。 */}
                {differing.length > 0 && (
                  <div className="rounded-md bg-warning-50 p-3">
                    <p className="font-bold text-warning-700">
                      数量が違う行が {differing.length} 行あります
                    </p>
                    <ul className="mt-1 flex flex-col gap-0.5 text-tiny text-default-600">
                      {differing.map((row) => (
                        <li key={row.project_item_id}>
                          {row.drawing_number} … 見積 {row.quotation_quantity} / 案件{" "}
                          {row.project_quantity}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-1 text-tiny text-default-500">
                      結び付けは行いますが、作っている数は案件の側が正です。直すなら
                      先に見積を直してください（受注にすると直せなくなります）。
                    </p>
                  </div>
                )}

                {/* 残る行は**理由まで**出す。「結ばれなかった」だけでは、図番を
                    直すのか行を選ぶのかが分からない。 */}
                {leftOver.length > 0 && (
                  <div className="rounded-md bg-default-50 p-3">
                    <p className="font-bold">結ばれずに残る行が {leftOver.length} 行</p>
                    <ul className="mt-1 flex flex-col gap-0.5 text-tiny text-default-600">
                      {leftOver.map((row) => (
                        <li key={`${row.side}-${row.item_id}`}>
                          {row.side} {row.drawing_number} {row.item_name} …{" "}
                          {row.reason === "ambiguous"
                            ? "同じ図番が相手側に複数あります"
                            : "相手の行がありません"}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-1 text-tiny text-default-500">
                      残った行は、受注にしたあと案件の明細で 1 行ずつ結べます。
                    </p>
                  </div>
                )}
              </div>
            )
          ) : (
            <>
              <Input
                label="案件名"
                labelPlacement="outside"
                placeholder=" "
                aria-label="案件名"
                variant="bordered"
                radius="sm"
                description="受注時点で決まっていなければ空のままで構いません。"
                value={title}
                onValueChange={(v) => {
                  setTitle(v);
                  setError(null);
                }}
              />

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Input
                  label="客先注文番号"
                  labelPlacement="outside"
                  placeholder=" "
                  aria-label="客先注文番号"
                  variant="bordered"
                  radius="sm"
                  value={poNumber}
                  onValueChange={setPoNumber}
                />
                <Input
                  label="受注日"
                  labelPlacement="outside"
                  placeholder=" "
                  aria-label="受注日"
                  type="date"
                  variant="bordered"
                  radius="sm"
                  value={orderDate}
                  onValueChange={setOrderDate}
                />
                <Input
                  label="納期"
                  labelPlacement="outside"
                  placeholder=" "
                  aria-label="納期"
                  type="date"
                  variant="bordered"
                  radius="sm"
                  description="各明細の納期にも同じ値が入ります。"
                  value={deadline}
                  onValueChange={setDeadline}
                />
                {/* 出荷予定日は案件だけが持つ（明細には無い）。納期と別に動く
                    ので、受注の時点で分かっているならここで入れておく。 */}
                <Input
                  label="出荷予定日"
                  labelPlacement="outside"
                  placeholder=" "
                  aria-label="出荷予定日"
                  type="date"
                  variant="bordered"
                  radius="sm"
                  value={shippingPlannedDate}
                  onValueChange={setShippingPlannedDate}
                />
              </div>

              <Textarea
                label="備考"
                labelPlacement="outside"
                placeholder=" "
                aria-label="備考"
                variant="bordered"
                radius="sm"
                minRows={2}
                value={note}
                onValueChange={setNote}
              />
            </>
          )}

          {error && <p className="text-sm text-danger">{error}</p>}
        </ModalBody>

        <ModalFooter>
          <Button variant="light" radius="full" data-testid="cancel" onPress={onClose}>
            キャンセル
          </Button>
          <Button
            color="primary"
            radius="full"
            data-testid="order-submit"
            // 1 行も結べない見積を受注にすると、見積だけが締まって直せなくなる。
            isDisabled={attaching && !preview.isPending && matched.length === 0}
            isLoading={saving}
            onPress={() => void submit()}
          >
            受注にする
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
